import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import { mailerPinsApi } from '../../lib/directMailApi';
import { getApiErrorMessage } from '../../lib/apiError';
import type { MailerPin } from '../../types/directMail';

const PRESET_REASONS = [
  'Metal roof',
  'Roof looks new',
  'Not a single-family home',
  'Wrong property',
  'Homeowner asked us not to contact them',
];

export interface SkipPinModalProps {
  open: boolean;
  pin: MailerPin | null;
  onClose: () => void;
  onSkipped: (pin: MailerPin) => void;
}

export const SkipPinModal: React.FC<SkipPinModalProps> = ({ open, pin, onClose, onSkipped }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open, pin]);

  const submit = async () => {
    if (!pin || !reason.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await mailerPinsApi.skip(pin.id, { reason: reason.trim() });
      onSkipped(updated);
    } catch (err) {
      setError(getApiErrorMessage(err, 'That pin could not be skipped.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: { xs: '92vw', sm: 460 } }}>
        <DialogTitle>Skip this address</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography level="body-sm">
              {pin?.formatted_address ?? ''}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              A skipped pin drops out of the queue and frees the address to be pinned again later.
            </Typography>

            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {PRESET_REASONS.map((preset) => (
                <Chip
                  key={preset}
                  variant={reason === preset ? 'solid' : 'soft'}
                  color={reason === preset ? 'primary' : 'neutral'}
                  onClick={() => setReason(preset)}
                >
                  {preset}
                </Chip>
              ))}
            </Stack>

            <FormControl>
              <FormLabel>Reason</FormLabel>
              <Input
                autoFocus
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void submit();
                  }
                }}
                placeholder="Why is this address not worth mailing?"
                slotProps={{ input: { maxLength: 255 } }}
              />
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="danger" loading={saving} disabled={!reason.trim()} onClick={() => void submit()}>
            Skip pin
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};
